import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ClipboardCheck, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * The 12-point pack score — its own screen, not a subsection of Where Money Went.
 *
 * "Missing" means not in the records we can see, never a finding that a charge is
 * improper (blocking rule 2) — this page uses only the backend's own wording, never
 * rephrases it. The disclaimer and the draft's "not sent, not legal advice" framing are
 * both required, not decorative (blocking rules 2 and 3).
 */

const STATUS_LABEL = { present: "Present", partial: "Partial", missing: "Missing" };
const STATUS_STYLE = {
    present: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-800 border-amber-200",
    missing: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function PackScore() {
    const { user } = useAuth();
    const isAdmin = user?.role === "estate_admin";
    const [raised, setRaised] = useState(null);
    const [raising, setRaising] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [periodId, setPeriodId] = useState(null);
    const [score, setScore] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadPeriods = useCallback(async () => {
        try {
            const res = await api.get("/finance/charge-periods");
            setPeriods(res.data);
            if (!periodId && res.data[0]) setPeriodId(res.data[0].id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load charge periods");
        } finally {
            setLoading(false);
        }
    }, [periodId]);

    const loadScore = useCallback(async () => {
        if (!periodId) {
            setScore(null);
            return;
        }
        try {
            const res = await api.get(`/finance/charge-periods/${periodId}/pack-score`);
            setScore(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load the pack score");
        }
    }, [periodId]);

    useEffect(() => { loadPeriods(); }, [loadPeriods]);
    useEffect(() => { loadScore(); }, [loadScore]);

    const copyDraft = () => {
        if (!score?.information_request_draft) return;
        navigator.clipboard?.writeText(score.information_request_draft);
        toast.success("Draft copied");
    };

    // Raises the draft as a tracked case (the backend stores score_pack's own text verbatim).
    // It is not sent to anyone: the director reviews it and chooses how to send it.
    const raiseRequest = async () => {
        setRaising(true);
        try {
            const res = await api.post(`/finance/charge-periods/${periodId}/information-request`, {});
            setRaised(res.data);
            toast.success("Tracked request created. It has not been sent to anyone.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create the request");
        } finally {
            setRaising(false);
        }
    };

    useEffect(() => { setRaised(null); }, [periodId]);

    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="pack-score-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <ClipboardCheck size={14} /> The 12-point pack score
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">
                Is the annual pack complete?
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Graded against 12 things a well-run estate's pack should contain. "Missing" means
                not in the records we can see — not a finding that anything is wrong.
            </p>

            {periods.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-1.5">
                    {periods.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setPeriodId(p.id)}
                            className={`text-xs px-2.5 py-1 rounded-full border ${
                                periodId === p.id
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white border-slate-200 text-slate-600"
                            }`}
                            data-testid={`pack-score-period-${p.id}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            )}

            {!loading && periods.length === 0 && (
                <div
                    className="mt-8 rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-600"
                    data-testid="pack-score-no-period"
                >
                    No charge period yet. Add one on{" "}
                    <Link to="/money" className="text-slate-900 font-medium underline">
                        Where money went
                    </Link>{" "}
                    to see a pack score for it.
                </div>
            )}

            {score && (
                <>
                    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 text-center">
                        <div className="font-heading text-5xl font-bold text-slate-900 tabular-nums" data-testid="pack-score-value">
                            {score.score}
                            <span className="text-2xl text-slate-400">/{score.out_of}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-3" data-testid="pack-score-headline">
                            {score.headline}
                        </p>
                    </div>

                    <ol className="mt-6 space-y-2">
                        {score.items.map((item) => (
                            <li
                                key={item.key}
                                className="rounded-lg border border-slate-200 bg-white p-4"
                                data-testid={`pack-score-item-${item.key}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <span className="font-medium text-slate-900">{item.title}</span>
                                    <span
                                        className={`shrink-0 text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLE[item.status] || STATUS_STYLE.missing}`}
                                    >
                                        {STATUS_LABEL[item.status] || item.status}
                                    </span>
                                </div>
                                {item.evidence?.length > 0 && (
                                    <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                                        {item.evidence.map((e, i) => (
                                            <li key={i}>{e}</li>
                                        ))}
                                    </ul>
                                )}
                                {item.ask && (
                                    <p className="text-xs text-slate-600 mt-2">
                                        <span className="text-slate-400 uppercase tracking-wide text-[10px] mr-1">Draft ask</span>
                                        {item.ask}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>

                    <section className="mt-8 rounded-lg border border-slate-200 bg-white p-5">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="font-heading text-lg font-semibold text-slate-900">
                                Information request
                            </h2>
                            <button
                                onClick={copyDraft}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 underline"
                                data-testid="pack-score-copy-draft"
                            >
                                <Copy size={12} /> Copy
                            </button>
                        </div>
                        <pre
                            className="mt-3 text-[11px] whitespace-pre-wrap text-slate-700 bg-slate-50 border border-slate-100 rounded-md p-3"
                            data-testid="pack-score-draft"
                        >
                            {score.information_request_draft}
                        </pre>
                        {isAdmin && (
                            <div className="mt-3 flex items-center gap-3">
                                {raised ? (
                                    <Link
                                        to={`/cases/${raised.id}`}
                                        className="text-sm underline text-slate-900"
                                        data-testid="pack-score-open-request"
                                    >
                                        Open the tracked request
                                    </Link>
                                ) : (
                                    <button
                                        onClick={raiseRequest}
                                        disabled={raising}
                                        className="rounded bg-slate-900 text-white px-3 py-1.5 text-sm disabled:opacity-60"
                                        data-testid="pack-score-create-request"
                                    >
                                        Create tracked request
                                    </button>
                                )}
                                <span className="text-xs text-slate-500">
                                    Saved as a case for you to review. Nothing is sent.
                                </span>
                            </div>
                        )}
                    </section>

                    <p className="mt-6 text-xs text-slate-500 max-w-2xl" data-testid="pack-score-disclaimer">
                        {score.disclaimer}
                    </p>
                </>
            )}
        </div>
    );
}
